








import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import MultilevelCategoriesFetcher from "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesFetchMixin";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";

// Types
import { IProduct, IProductCategory } from "@/models/products";
import { IState } from "@/store";
import { ICrumb } from "@/components/app-base/UBreadcrumbs.vue";

@Component({
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      }
    })
  }
})
export default class ProductCatalogueBreadcrumbs extends Mixins(
  RequestPromises,
  MultilevelCategoriesFetcher
) {
  // Getters
  public product!: IProduct;
  public productScope!: string;

  // Props
  @Prop({ required: true })
  public productId!: IProduct["id"];

  // Data
  public isLoading = false;

  public async created() {
    if (!this.contentAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await Promise.all([this.getRootCategories(), this.getProductData()]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
    }
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored: ignoreStored
    });
  }

  public get parentCategories() {
    return this.getProductPath(
      this.rootCategories,
      this.$_.get(this.product, "category_id")
    );
  }

  public getProductPath(categories, categoryId: IProductCategory["id"]) {
    let path = [];

    function dive(_data, _path, _, level) {
      for (let i = 0; i < _data.length; i++) {
        if (_data[i].id === categoryId) {
          path = _.concat(_path, [
            { id: _data[i].id, name: _data[i].name, order: level }
          ]);
        } else if (_.has(_data[i], "subcategories")) {
          dive(
            _data[i].subcategories,
            _.concat(_path, [
              { id: _data[i].id, name: _data[i].name, order: level }
            ]),
            _,
            level + 1
          );
        }
      }
    }

    dive(categories, [], this.$_, 0);

    return path;
  }

  public get breadcrumbs(): ICrumb[] {
    return [
      ...this.$_.map(this.parentCategories, (category: any) => ({
        label: category.name,
        route: {
          to: {
            name: this.routeName,
            query: {
              CT1: this.getRouteQuery(category.id)
            }
          }
        }
      })),
      {
        label: this.product.name
      }
    ];
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get routeName() {
    if (this.isOption) return "adminCatalogueOptions";
    if (this.isAttribute) return "adminCatalogueAttributes";
    return "adminCatalogueProducts";
  }

  public getRouteQuery(categoryId) {
    const order = this.$_.get(
      this.$_.find(this.parentCategories, ["id", categoryId]),
      "order",
      0
    );

    return (
      categoryId +
      ":10" +
      (order
        ? "|" +
          this.getRouteQuery(
            this.$_.get(
              this.$_.find(this.parentCategories, ["order", order - 1]),
              "id"
            )
          )
        : "")
    );
  }

  public get contentAvailable() {
    const permissions = ["staff_get_product"];

    if (this.isOption) {
      permissions.push("list_product_options_categories");
    } else if (this.isAttribute) {
      permissions.push("list_product_attributes_categories");
    } else {
      permissions.push("list_product_categories");
    }

    return this.$userCan(...permissions);
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  }
}
